import { GTBookingListItem, IEditBookingValues } from 'features/Bookings/components/EditBooking/types'
import { ITrip } from 'features/Trips/types'
import { GetBookingsQuery } from 'gql/generated/graphql'
import {
  GTBookingsDashboardDataMethodInfo,
  GTBookingV2Passenger,
  GTBookingV2Ticket,
  GTBookingV2PassengerType,
  GTBookingV2,
  GTBookingV2TripSegment,
  GTBookingV2Trip,
  GTBookingV2TripSegmentPlace,
  GTBookingV2TicketStatus
} from 'gql/types.generated'

export interface IAncillary {
  type: string
  status: string
  paid: boolean
  subtotal: number
  vat: number
}

export interface IPassenger {
  firstName?: string
  lastName?: string
  typeV2: GTBookingV2PassengerType
}

export interface ISeat {
  id: string
  label: string
  status: string
  preferredSeatPrice?: number
}

export interface ITicket {
  id: string
  status: GTBookingV2TicketStatus
  subtotal: number
  vat: number
  discount: number
  passenger: IPassenger
  seat: ISeat
  trip: ITrip
  ancillaries: IAncillary[]
  updatedCount?: number
}

export enum ESalesChannel {
  KOLORS_MX = 'KOLORS_MX',
  KOLORS_US = 'KOLORS_US',
  POS = 'POS'
}

export enum EConfirmationModality {
  RESERVED = 'reserved',
  PAID = 'paid',
  FACEBOOK = 'facebook'
}

export interface IPaymentTickets {
  id: string
  subtotal: number
  vat: number
  discount: number
}
export interface IPayment {
  status: string
  tickets: IPaymentTickets[]
}

export interface IBooking {
  id: string
  departureTrips: ITrip[]
  returnTrips: ITrip[]
  tickets: ITicket[]
  payments: IPayment[]
}

export interface GTBookingsDashboardDataMethodInfoExtended extends GTBookingsDashboardDataMethodInfo {
  total?: {
    count: number
    amount: number
  }
}
export interface GTBookingsDashboardDataExtended {
  cancelled: GTBookingsDashboardDataMethodInfoExtended
  confirmed: GTBookingsDashboardDataMethodInfoExtended
  failed: GTBookingsDashboardDataMethodInfoExtended
  partiallyConfirmed: GTBookingsDashboardDataMethodInfoExtended
  reserved: GTBookingsDashboardDataMethodInfoExtended
  total?: GTBookingsDashboardDataMethodInfo
  onlinePercent?: number
  offlinePercent?: number
}

export interface IPrevBookingsDashboard {
  bookingsDashboard: {
    day: GTBookingsDashboardDataExtended
    month: GTBookingsDashboardDataExtended
  }
}

export interface IBookingsDashboardSubscriptionData {
  bookingsDashboard: {
    amount: number
    bookingID: string
    createdAt: Date
    previous: {
      status: string
      amount: number
    }
    saleChannel: Exclude<keyof GTBookingsDashboardDataMethodInfo, '__typename'>
    status: string
  }
}

export interface IBookingsDashboardSubscription {
  data: IBookingsDashboardSubscriptionData
}

export type TPassengerTickets = {
  tickets: GTBookingV2Ticket[]
}
export type TPassengerWithTickets = GTBookingV2Passenger & TPassengerTickets

export type TMovedBookingTripPlace = Pick<GTBookingV2TripSegmentPlace, 'id' | 'name' | 'city'>
export interface IMovedBookingTripSegment extends Pick<GTBookingV2TripSegment, 'id' | 'departureAt' | 'fare'> {
  origin: TMovedBookingTripPlace
  destination: TMovedBookingTripPlace
}
export interface IMovedBookingTrip extends Pick<GTBookingV2Trip, 'id'> {
  segment: IMovedBookingTripSegment
  departureDate?: string
  departureTime?: string
}

export interface IMovedBookingTripTicket extends Pick<GTBookingV2Trip, 'id'> {
  segment: Pick<IMovedBookingTripSegment, 'id'>
}

export interface IMovedBookingTicket
  extends Pick<
    GTBookingV2Ticket,
    | 'id'
    | 'subtotal'
    | 'vat'
    | 'discount'
    | 'status'
    | 'isCheckedIn'
    | 'passenger'
    | 'seat'
    | 'ancillaries'
    | 'serviceFee'
  > {
  trip: IMovedBookingTripTicket
  isReturn?: boolean
}

export interface IMovedBooking
  extends Pick<GTBookingV2, 'id' | 'salesChannel' | 'buyer' | 'coupon' | 'payments' | 'refunds' | 'vertical'> {
  departureTrips: IMovedBookingTrip[]
  returnTrips: IMovedBookingTrip[]
  tickets: IMovedBookingTicket[]
}

export interface ITicketModel extends GTBookingV2Ticket {
  paidAmount: number
}
export interface IEditBookingModel extends GTBookingListItem {
  toFormValues: () => IEditBookingValues
}
export interface IBookingModel extends Omit<GTBookingV2, 'tickets'> {
  tickets: ITicketModel[]
  returnTickets: GTBookingV2Ticket[]
  hasReturnTrips: boolean
  hasReturnTickets: boolean
  hasDepartureTrips: boolean
  hasDepartureTickets: boolean
  hasPaidStatus: boolean
  hasReservation: boolean
  isBookingCancelled: boolean
  isCancelledByUser: boolean
  isPartiallyCancelledByUser: boolean
  getBookingsActions: () => string[]
  canApplyInsurance: boolean
  canCancelInsurance: boolean
}

export interface IRegisterBookingTaxInvoicedValues {
  taxFolio: string
}

export type GTBookingItem = GetBookingsQuery['bookingsV2']['items'][0]
